/**
=========================================================
* Material Dashboard 3 PRO React - v2.4.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect, useRef, useMemo } from "react";
import axios from "axios";
import swal from "sweetalert";
import jsPDF from "jspdf";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import CircularProgress from "@mui/material/CircularProgress";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import Autocomplete from "@mui/material/Autocomplete";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

// Material Dashboard 3 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDEditor from "components/MDEditor";
import MDDropzone from "components/MDDropzone";
import MDProgress from "components/MDProgress";

// Material Dashboard 3 PRO React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

// Icons
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import PersonIcon from "@mui/icons-material/Person";
import AssessmentIcon from "@mui/icons-material/Assessment";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PeopleIcon from "@mui/icons-material/People";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import PhotoLibraryIcon from "@mui/icons-material/PhotoLibrary";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import LinkIcon from "@mui/icons-material/Link";
import SearchIcon from "@mui/icons-material/Search";

import { logoutUser } from "actions/AuthAction";
import { useNavigate } from "react-router-dom";
import { API_ESA_OFFICE, API_ESA_OFFICE_UPLOAD } from "utils";

// Helper untuk format datetime
function formatDateTime(dateStr) {
  if (!dateStr) return "-";
  const date = new Date(dateStr);
  if (isNaN(date)) return "-";
  return date.toLocaleString("id-ID", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// Helper untuk memotong nama dinas menjadi 3 kata
function truncateDinasName(nama) {
  if (!nama) return "";
  const words = nama.trim().split(/\s+/);
  if (words.length <= 3) return nama;
  return words.slice(0, 3).join(" ") + "...";
}

function EventSaya() {
  const [detailEventsList, setDetailEventsList] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [openEditDetailDialog, setOpenEditDetailDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingDetailEvent, setEditingDetailEvent] = useState(null);
  const [dinasId, setDinasId] = useState(null);
  const [userId, setUserId] = useState(null);
  const [userNamaDinas, setUserNamaDinas] = useState("");
  const [dinasList, setDinasList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submittingDetail, setSubmittingDetail] = useState(false);
  const [tahun, setTahun] = useState(new Date().getFullYear());
  const [bulan, setBulan] = useState(new Date().getMonth() + 1);
  const [searchQuery, setSearchQuery] = useState("");
  const [eventStatistics, setEventStatistics] = useState({}); // { eventId: statistics }
  const navigate = useNavigate();
  const dropzoneRef = useRef(null);

  // Mobile detection
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [formData, setFormData] = useState({
    notulen: "",
    foto: [],
    status: "",
    dokumenUrl: "",
    dokumenFile: null,
  });

  const [detailFormData, setDetailFormData] = useState({
    detail_kegiatan: "",
    waktu_mulai: "",
    waktu_finish: "",
    tempat: "",
    dinas_id: null,
  });

  // State untuk target dinas di dialog edit
  const [targetDinasForEdit, setTargetDinasForEdit] = useState([]);

  // State untuk foto existing dan yang akan dihapus
  const [existingFotos, setExistingFotos] = useState([]);
  const [removedFotoUrls, setRemovedFotoUrls] = useState([]);

  // State untuk dialog view notulen/foto
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [viewingDetailEvent, setViewingDetailEvent] = useState(null);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);

  // State untuk dialog progress MD
  const [openProgressDialog, setOpenProgressDialog] = useState(false);
  const [viewingProgressMD, setViewingProgressMD] = useState("");

  // State untuk dialog daftar peserta
  const [openPesertaDialog, setOpenPesertaDialog] = useState(false);
  const [daftarPeserta, setDaftarPeserta] = useState([]);
  const [loadingPeserta, setLoadingPeserta] = useState(false);
  const [selectedDetailEventId, setSelectedDetailEventId] = useState(null);

  // State untuk dokumen laporan & foto viewer
  const [existingDokumen, setExistingDokumen] = useState(null);
  const [removedExistingDokumen, setRemovedExistingDokumen] = useState(false);
  const [openFotoDialog, setOpenFotoDialog] = useState(false);
  const [viewingFotos, setViewingFotos] = useState([]);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (user) {
      setDinasId(user.dinas_id);
      // Pastikan uid di-set sebagai string untuk konsistensi
      setUserId(user.uid ? String(user.uid) : null);
      setUserNamaDinas(user.nama_dinas || "");

      // Fetch dinas list untuk Autocomplete
      axios
        .get(`${API_ESA_OFFICE}dinas`)
        .then((res) => {
          if (res.data && Array.isArray(res.data)) {
            setDinasList(res.data.map((d) => d.nama_dinas || d.nama));
          }
        })
        .catch((err) => {
          console.error("Error fetching dinas list:", err);
        });
    } else {
      logoutUser(navigate);
    }
  }, []);

  const fetchDetailEvents = async () => {
    if (!dinasId || !userId) return;

    setLoading(true);
    try {
      let url = `${API_ESA_OFFICE}events/detail/all?dinasId=${dinasId}&userId=${userId}`;
      if (tahun) {
        url += `&tahun=${tahun}`;
      }
      if (bulan) {
        url += `&bulan=${bulan}`;
      }
      const response = await axios.get(url);
      const data = response.data || [];
      // Pastikan id_pembuat selalu string untuk konsistensi
      // Pastikan id_events ada di setiap item
      const formattedData = data.map((item) => ({
        ...item,
        id_pembuat: item.id_pembuat ? String(item.id_pembuat) : null,
        id_events: item.id_events || null,
        dinas_peserta_nama: item.dinas_peserta_nama || null,
        event_nama_pembuat: item.event_nama_pembuat || null,
        event_uid_RSS_pembuat: item.event_uid_RSS_pembuat || null,
        event_id_pembuat: item.event_id_pembuat || null,
      }));
      console.log(
        "fetchDetailEvents - formattedData sample:",
        formattedData.length > 0 ? formattedData[0] : "No data"
      );
      setDetailEventsList(formattedData);
    } catch (error) {
      console.error("Error fetching detail events:", error);
      swal({
        title: "Error",
        text: "Gagal mengambil data detail events",
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (dinasId && userId) {
      fetchDetailEvents();
    }
  }, [dinasId, userId, tahun, bulan]);

  const handleOpenDialog = (detailEvent) => {
    setEditingId(detailEvent.id);
    setFormData({
      notulen: detailEvent.notulen || "",
      foto: [],
      status: detailEvent.status || "",
      dokumenUrl: "",
      dokumenFile: null,
    });
    // Set foto existing
    const fotos =
      detailEvent.foto && Array.isArray(detailEvent.foto)
        ? detailEvent.foto
        : [];
    setExistingFotos(fotos);
    setRemovedFotoUrls([]);
    if (dropzoneRef.current && dropzoneRef.current.dropzone) {
      dropzoneRef.current.dropzone.removeAllFiles(true);
    }
    setExistingDokumen(detailEvent.dokumen || null);
    setRemovedExistingDokumen(false);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingId(null);
    setFormData({
      notulen: "",
      foto: [],
      status: "",
      dokumenUrl: "",
      dokumenFile: null,
    });
    setExistingFotos([]);
    setRemovedFotoUrls([]);
    if (dropzoneRef.current && dropzoneRef.current.dropzone) {
      dropzoneRef.current.dropzone.removeAllFiles(true);
    }
    setExistingDokumen(null);
    setRemovedExistingDokumen(false);
    setSubmitting(false);
  };

  const handleRemoveExistingFoto = (fotoUrl) => {
    setExistingFotos((prev) => prev.filter((url) => url !== fotoUrl));
    setRemovedFotoUrls((prev) => [...prev, fotoUrl]);
  };

  const handleOpenFotoDialog = (detailEvent) => {
    const fotos =
      detailEvent.foto && Array.isArray(detailEvent.foto)
        ? detailEvent.foto
        : [];
    if (!fotos.length) return;
    setViewingFotos(fotos);
    setOpenFotoDialog(true);
  };

  const handleCloseFotoDialog = () => {
    setOpenFotoDialog(false);
    setViewingFotos([]);
  };

  const handleOpenDokumen = (dokumenPathOrUrl) => {
    if (!dokumenPathOrUrl) return;
    let url = dokumenPathOrUrl.trim();
    const isExternal = /^https?:\/\//i.test(url);
    if (!isExternal) {
      if (url.startsWith("/")) {
        url = `${API_ESA_OFFICE_UPLOAD}${url}`;
      } else {
        url = `${API_ESA_OFFICE_UPLOAD}/${url}`;
      }
    }
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const generatePDF = async (detailEvent) => {
    // Fetch data peserta
    let pesertaList = [];
    try {
      const pesertaResponse = await axios.get(
        `${API_ESA_OFFICE}attendance/${detailEvent.id}`
      );
      pesertaList = pesertaResponse.data || [];
    } catch (error) {
      console.error("Error fetching peserta:", error);
    }
    const doc = new jsPDF();
    let yPosition = 20;

    // Helper function untuk render HTML dengan mempertahankan formatting
    const renderHtmlContent = (html, x, y, maxWidth) => {
      if (!html) return y;

      let currentY = y;
      const lineHeight = 5;
      const paragraphSpacing = 3;

      // Set default font normal
      doc.setFont("helvetica", "normal");
      doc.setFontSize(11);

      // Parse HTML
      const tmp = document.createElement("DIV");
      tmp.innerHTML = html;

      // Track current font state
      let currentFontStyle = "normal"; // "normal", "bold", "italic"

      // Process nodes recursively
      const processNode = (node, indent = 0, parentFontStyle = "normal") => {
        if (node.nodeType === Node.TEXT_NODE) {
          const text = node.textContent.trim();
          if (text) {
            // Check if need new page
            if (currentY > 270) {
              doc.addPage();
              currentY = 20;
            }

            // Get computed style untuk text alignment
            const parent = node.parentElement;
            const textAlign =
              parent?.style?.textAlign ||
              window.getComputedStyle(parent || document.body).textAlign ||
              "left";

            // Set font sesuai parent font style
            if (parentFontStyle === "bold") {
              doc.setFont("helvetica", "bold");
            } else if (parentFontStyle === "italic") {
              doc.setFont("helvetica", "italic");
            } else {
              doc.setFont("helvetica", "normal");
            }

            // Split text to fit width
            const lines = doc.splitTextToSize(text, maxWidth - indent * 5);

            // Render each line dengan alignment
            lines.forEach((line) => {
              if (currentY > 270) {
                doc.addPage();
                currentY = 20;
              }

              let xPos = x + indent * 5;
              if (textAlign === "center") {
                xPos = 105; // Center of page
                doc.text(line, xPos, currentY, { align: "center" });
              } else if (textAlign === "right") {
                xPos = x + maxWidth;
                doc.text(line, xPos, currentY, { align: "right" });
              } else {
                doc.text(line, xPos, currentY);
              }
              currentY += lineHeight;
            });
          }
        } else if (node.nodeType === Node.ELEMENT_NODE) {
          const tagName = node.tagName?.toLowerCase();

          // Handle different HTML elements
          if (tagName === "p" || tagName === "div") {
            // Add spacing before paragraph (except first)
            if (currentY !== y && node.previousSibling) {
              currentY += paragraphSpacing;
            }

            // Process children dengan font style dari parent
            Array.from(node.childNodes).forEach((child) => {
              processNode(child, indent, parentFontStyle);
            });

            // Add spacing after paragraph
            currentY += paragraphSpacing;
          } else if (tagName === "br") {
            currentY += lineHeight;
          } else if (tagName === "strong" || tagName === "b") {
            // Bold text - pass "bold" to children
            Array.from(node.childNodes).forEach((child) => {
              processNode(child, indent, "bold");
            });
          } else if (tagName === "em" || tagName === "i") {
            // Italic text - pass "italic" to children
            Array.from(node.childNodes).forEach((child) => {
              processNode(child, indent, "italic");
            });
          } else if (tagName === "ul" || tagName === "ol") {
            // List
            Array.from(node.children).forEach((child, index) => {
              if (currentY > 270) {
                doc.addPage();
                currentY = 20;
              }

              // Set font normal untuk bullet
              doc.setFont("helvetica", "normal");
              const bullet = tagName === "ul" ? "•" : `${index + 1}.`;
              doc.text(bullet, x + indent * 5, currentY);
              processNode(child, indent + 1, parentFontStyle);
            });
          } else if (tagName === "li") {
            // List item
            Array.from(node.childNodes).forEach((child) => {
              processNode(child, indent, parentFontStyle);
            });
          } else if (tagName === "h1" || tagName === "h2" || tagName === "h3") {
            // Headings - always bold
            if (currentY !== y && node.previousSibling) {
              currentY += paragraphSpacing * 2;
            }
            const fontSize = tagName === "h1" ? 16 : tagName === "h2" ? 14 : 12;
            doc.setFontSize(fontSize);
            doc.setFont("helvetica", "bold");
            Array.from(node.childNodes).forEach((child) => {
              processNode(child, indent, "bold");
            });
            doc.setFontSize(11);
            doc.setFont("helvetica", "normal");
            currentY += paragraphSpacing;
          } else {
            // Process other elements - maintain parent font style
            Array.from(node.childNodes).forEach((child) => {
              processNode(child, indent, parentFontStyle);
            });
          }
        }
      };

      // Process all nodes dengan font normal sebagai default
      Array.from(tmp.childNodes).forEach((child) => {
        processNode(child, 0, "normal");
      });

      // Reset font ke normal setelah selesai
      doc.setFont("helvetica", "normal");
      doc.setFontSize(11);

      return currentY;
    };

    // Header
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text("Laporan Event", 105, yPosition, { align: "center" });
    yPosition += 10;

    // Informasi Event
    doc.setFontSize(12);
    doc.setFont("helvetica", "normal");
    doc.text(`Nama Event: ${detailEvent.kegiatan || "-"}`, 14, yPosition);
    yPosition += 7;
    doc.text(
      `Detail Kegiatan: ${detailEvent.detail_kegiatan || "-"}`,
      14,
      yPosition
    );
    yPosition += 7;
    doc.text(`Tempat: ${detailEvent.tempat || "-"}`, 14, yPosition);
    yPosition += 7;

    const waktuMulai = detailEvent.waktu_mulai
      ? new Date(detailEvent.waktu_mulai).toLocaleString("id-ID")
      : "-";
    const waktuFinish = detailEvent.waktu_finish
      ? new Date(detailEvent.waktu_finish).toLocaleString("id-ID")
      : "-";
    doc.text(`Waktu Mulai: ${waktuMulai}`, 14, yPosition);
    yPosition += 7;
    doc.text(`Waktu Selesai: ${waktuFinish}`, 14, yPosition);
    yPosition += 10;

    // Notulen
    if (detailEvent.notulen && detailEvent.notulen.trim() !== "") {
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.text("Notulen", 14, yPosition);
      yPosition += 7;

      // Check if need new page
      if (yPosition > 250) {
        doc.addPage();
        yPosition = 20;
      }

      // Render HTML content dengan mempertahankan formatting
      yPosition = renderHtmlContent(detailEvent.notulen, 14, yPosition, 180);
    }

    // Foto
    if (
      detailEvent.foto &&
      Array.isArray(detailEvent.foto) &&
      detailEvent.foto.length > 0
    ) {
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.text(`Foto Kegiatan (${detailEvent.foto.length})`, 14, yPosition);
      yPosition += 10;

      for (let i = 0; i < detailEvent.foto.length; i++) {
        const fotoUrl = detailEvent.foto[i];
        const fullUrl = fotoUrl.startsWith("/")
          ? `${API_ESA_OFFICE_UPLOAD}${fotoUrl}`
          : `${API_ESA_OFFICE_UPLOAD}/${fotoUrl}`;

        try {
          // Load image
          const img = await new Promise((resolve, reject) => {
            const image = new Image();
            image.crossOrigin = "anonymous";
            image.onload = () => resolve(image);
            image.onerror = reject;
            image.src = fullUrl;
          });

          // Calculate image dimensions to fit page width
          const maxWidth = 180;
          const maxHeight = 100;
          let imgWidth = img.width;
          let imgHeight = img.height;
          const ratio = Math.min(maxWidth / imgWidth, maxHeight / imgHeight);
          imgWidth = imgWidth * ratio;
          imgHeight = imgHeight * ratio;

          // Check if need new page
          if (yPosition + imgHeight > 270) {
            doc.addPage();
            yPosition = 20;
          }

          // Add image
          doc.text(`Foto ${i + 1}:`, 14, yPosition);
          yPosition += 5;
          doc.addImage(img, "JPEG", 14, yPosition, imgWidth, imgHeight);
          yPosition += imgHeight + 10;

          // Check if need new page after image
          if (yPosition > 250 && i < detailEvent.foto.length - 1) {
            doc.addPage();
            yPosition = 20;
          }
        } catch (error) {
          console.error("Error loading image:", error);
          doc.text(`Foto ${i + 1}: Gagal memuat gambar`, 14, yPosition);
          yPosition += 7;
        }
      }
    }

    // Daftar Peserta
    if (pesertaList.length > 0) {
      // Check if need new page
      if (yPosition > 220) {
        doc.addPage();
        yPosition = 20;
      }

      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.text(`Daftar Peserta (${pesertaList.length} orang)`, 14, yPosition);
      yPosition += 10;

      // Table header
      doc.setFontSize(10);
      doc.setFont("helvetica", "bold");
      const tableStartY = yPosition;
      doc.text("No", 14, yPosition);
      doc.text("Nama", 25, yPosition);
      doc.text("Instansi", 90, yPosition);
      doc.text("Waktu", 150, yPosition);
      yPosition += 6;

      // Table rows
      doc.setFont("helvetica", "normal");
      pesertaList.forEach((peserta, index) => {
        // Check if need new page
        if (yPosition > 270) {
          doc.addPage();
          yPosition = 20;
          // Redraw header
          doc.setFont("helvetica", "bold");
          doc.text("No", 14, yPosition);
          doc.text("Nama", 25, yPosition);
          doc.text("Instansi", 90, yPosition);
          doc.text("Waktu", 150, yPosition);
          yPosition += 6;
          doc.setFont("helvetica", "normal");
        }

        const nama = peserta.nama || "-";
        const instansi = peserta.instansi || "-";
        const waktu = peserta.waktu
          ? new Date(peserta.waktu).toLocaleString("id-ID", {
              day: "numeric",
              month: "short",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            })
          : "-";

        const currentY = yPosition;
        doc.text(`${index + 1}`, 14, currentY);
        // Split long text
        const namaLines = doc.splitTextToSize(nama, 60);
        const instansiLines = doc.splitTextToSize(instansi, 55);
        const waktuLines = doc.splitTextToSize(waktu, 35);

        const maxLines = Math.max(
          namaLines.length,
          instansiLines.length,
          waktuLines.length
        );
        const lineHeight = 5;

        for (let i = 0; i < maxLines; i++) {
          const rowY = currentY + i * lineHeight;
          doc.text(namaLines[i] || "", 25, rowY);
          doc.text(instansiLines[i] || "", 90, rowY);
          doc.text(waktuLines[i] || "", 150, rowY);
        }

        yPosition = currentY + maxLines * lineHeight + 2;
      });

      // Draw table border
      doc.setDrawColor(200, 200, 200);
      doc.setLineWidth(0.1);
      const tableEndY = yPosition - 2;
      doc.line(14, tableStartY - 4, 14, tableEndY); // Left border
      doc.line(180, tableStartY - 4, 180, tableEndY); // Right border
      doc.line(14, tableStartY - 4, 180, tableStartY - 4); // Top border
      doc.line(14, tableEndY, 180, tableEndY); // Bottom border
      doc.line(23, tableStartY - 4, 23, tableEndY); // No border
      doc.line(88, tableStartY - 4, 88, tableEndY); // Nama border
      doc.line(148, tableStartY - 4, 148, tableEndY); // Instansi border
    }

    // Generate PDF blob
    const pdfBlob = doc.output("blob");
    const url = URL.createObjectURL(pdfBlob);
    return url;
  };

  const handleOpenViewDialog = async (detailEvent) => {
    setViewingDetailEvent(detailEvent);
    setOpenViewDialog(true);

    // Generate PDF
    try {
      const pdfUrl = await generatePDF(detailEvent);
      setPdfBlobUrl(pdfUrl);
    } catch (error) {
      console.error("Error generating PDF:", error);
      swal({
        title: "Error",
        text: "Gagal membuat PDF",
        icon: "error",
      });
    }
  };

  const handleCloseViewDialog = () => {
    // Clean up blob URL
    if (pdfBlobUrl) {
      URL.revokeObjectURL(pdfBlobUrl);
      setPdfBlobUrl(null);
    }
    setOpenViewDialog(false);
    setViewingDetailEvent(null);
  };

  const handleOpenPesertaDialog = async (detailEventId, jumlahPeserta) => {
    if (jumlahPeserta === 0) return;

    setSelectedDetailEventId(detailEventId);
    setOpenPesertaDialog(true);
    setLoadingPeserta(true);
    setDaftarPeserta([]);

    try {
      const response = await axios.get(
        `${API_ESA_OFFICE}attendance/${detailEventId}`
      );
      setDaftarPeserta(response.data || []);
    } catch (error) {
      console.error("Error fetching daftar peserta:", error);
      swal({
        title: "Error",
        text: "Gagal mengambil data peserta",
        icon: "error",
      });
      setDaftarPeserta([]);
    } finally {
      setLoadingPeserta(false);
    }
  };

  const handleClosePesertaDialog = () => {
    setOpenPesertaDialog(false);
    setDaftarPeserta([]);
    setSelectedDetailEventId(null);
  };

  const handleOpenEditDetailDialog = async (detailEvent) => {
    console.log("handleOpenEditDetailDialog - detailEvent:", detailEvent);
    if (!detailEvent.id_events || !detailEvent.id) {
      console.error("Missing id_events or id in detailEvent:", detailEvent);
      swal({
        title: "Error",
        text: "Data detail event tidak valid. id_events atau id tidak ditemukan.",
        icon: "error",
      });
      return;
    }

    const isEvaluasiOrAsistensi =
      detailEvent.status_event === "evaluasi" ||
      detailEvent.status_event === "asistensi";

    // Fetch target dinas jika evaluasi/asistensi
    let targetDinas = [];
    if (isEvaluasiOrAsistensi) {
      try {
        const response = await axios.get(
          `${API_ESA_OFFICE}events/${detailEvent.id_events}/target-dinas`
        );
        targetDinas = (response.data || []).filter(
          (d) => d.dinas_id && d.nama_dinas
        );
      } catch (error) {
        console.error("Error fetching target dinas:", error);
        targetDinas = [];
      }
    }
    setTargetDinasForEdit(targetDinas);

    setEditingDetailEvent(detailEvent);
    setDetailFormData({
      detail_kegiatan: detailEvent.detail_kegiatan || "",
      waktu_mulai: detailEvent.waktu_mulai
        ? new Date(detailEvent.waktu_mulai).toISOString().slice(0, 16)
        : "",
      waktu_finish: detailEvent.waktu_finish
        ? new Date(detailEvent.waktu_finish).toISOString().slice(0, 16)
        : "",
      tempat: detailEvent.tempat || userNamaDinas || "",
      dinas_id: detailEvent.dinas_id || null,
    });
    setOpenEditDetailDialog(true);
  };

  const handleCloseEditDetailDialog = () => {
    setOpenEditDetailDialog(false);
    setEditingDetailEvent(null);
    setTargetDinasForEdit([]);
    setDetailFormData({
      detail_kegiatan: "",
      waktu_mulai: "",
      waktu_finish: "",
      tempat: "",
      dinas_id: null,
    });
    setSubmittingDetail(false);
  };

  const handleDetailFormChange = (field, value) => {
    setDetailFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmitEditDetail = async () => {
    if (submittingDetail || !editingDetailEvent) return;

    // Validasi
    if (!detailFormData.waktu_mulai) {
      swal({
        title: "Error",
        text: "Waktu mulai harus diisi",
        icon: "error",
      });
      return;
    }

    // Validasi id_events dan id
    if (!editingDetailEvent.id_events || !editingDetailEvent.id) {
      swal({
        title: "Error",
        text: "Data detail event tidak valid",
        icon: "error",
      });
      console.error("editingDetailEvent:", editingDetailEvent);
      return;
    }

    const isEvaluasiOrAsistensi =
      editingDetailEvent.status_event === "evaluasi" ||
      editingDetailEvent.status_event === "asistensi";

    // Validasi dinas_id untuk evaluasi/asistensi
    if (isEvaluasiOrAsistensi && !detailFormData.dinas_id) {
      swal({
        title: "Error",
        text: "Dinas peserta harus dipilih untuk event evaluasi atau asistensi",
        icon: "error",
      });
      return;
    }

    setSubmittingDetail(true);
    try {
      const payload = {
        detail_kegiatan: detailFormData.detail_kegiatan || null,
        waktu_mulai: detailFormData.waktu_mulai,
        waktu_finish: detailFormData.waktu_finish || null,
        tempat: detailFormData.tempat || null,
      };

      // Tambahkan dinas_id jika evaluasi/asistensi
      if (isEvaluasiOrAsistensi) {
        payload.dinas_id = detailFormData.dinas_id;
      }

      await axios.put(
        `${API_ESA_OFFICE}events/${editingDetailEvent.id_events}/detail/${editingDetailEvent.id}`,
        payload
      );

      swal({
        title: "Berhasil!",
        text: "Detail event berhasil diupdate",
        icon: "success",
        button: "OK",
      });

      handleCloseEditDetailDialog();
      fetchDetailEvents();
    } catch (error) {
      console.error("Error updating detail event:", error);
      swal({
        title: "Error!",
        text: error.response?.data?.message || "Gagal mengupdate detail event",
        icon: "error",
        button: "OK",
      });
    } finally {
      setSubmittingDetail(false);
    }
  };

  const handleDeleteDetailEvent = async (detailEvent) => {
    swal({
      title: "Apakah Anda yakin?",
      text: "Data detail event akan dihapus secara permanen!",
      icon: "warning",
      buttons: ["Batal", "Ya, Hapus!"],
      dangerMode: true,
    }).then(async (willDelete) => {
      if (willDelete) {
        try {
          await axios.delete(
            `${API_ESA_OFFICE}events/detail/${detailEvent.id}`
          );
          swal({
            title: "Berhasil!",
            text: "Detail event berhasil dihapus",
            icon: "success",
            button: "OK",
          });
          fetchDetailEvents();
        } catch (error) {
          swal({
            title: "Error!",
            text:
              error.response?.data?.message ||
              "Terjadi kesalahan saat menghapus detail event",
            icon: "error",
            button: "OK",
          });
        }
      }
    });
  };

  const handleDropzoneChange = (files) => {
    // Hitung total file (existing + baru)
    const totalFiles = existingFotos.length + (files ? files.length : 0);

    // Validasi maksimal 3 file total
    if (totalFiles > 3) {
      swal({
        title: "Error",
        text: "Maksimal 3 foto total (termasuk foto yang sudah ada)",
        icon: "error",
      });
      if (dropzoneRef.current && dropzoneRef.current.dropzone) {
        dropzoneRef.current.dropzone.removeAllFiles(true);
      }
      return;
    }

    // Validasi ukuran file (maksimal 1.5MB per file)
    if (files && files.length > 0) {
      const oversizedFiles = files.filter((file) => {
        const fileObj = file.file || file;
        return fileObj.size > 1.5 * 1024 * 1024;
      });

      if (oversizedFiles.length > 0) {
        swal({
          title: "Error",
          text: "Ukuran file melebihi 1.5 MB. Silakan pilih file yang lebih kecil",
          icon: "error",
        });
        if (dropzoneRef.current && dropzoneRef.current.dropzone) {
          dropzoneRef.current.dropzone.removeAllFiles(true);
        }
        return;
      }
    }

    setFormData((prev) => ({
      ...prev,
      foto: files || [],
    }));
  };

  const handleSubmit = async () => {
    if (submitting) return;

    setSubmitting(true);
    try {
      // Validasi status pelaksanaan
      if (!formData.status) {
        swal({
          title: "Error",
          text: "Status pelaksanaan harus dipilih (proses atau selesai)",
          icon: "error",
        });
        setSubmitting(false);
        return;
      }

      // Validasi kombinasi dokumen (tidak boleh isi URL dan upload file sekaligus)
      const trimmedDokUrl = formData.dokumenUrl
        ? formData.dokumenUrl.trim()
        : "";
      if (trimmedDokUrl && formData.dokumenFile) {
        swal({
          title: "Error",
          text: "Pilih salah satu: upload file dokumen atau isi URL dokumen, bukan keduanya.",
          icon: "error",
        });
        setSubmitting(false);
        return;
      }

      // 1. Upload foto jika ada
      let fotoUrls = [];
      if (formData.foto && formData.foto.length > 0) {
        const uploadPromises = formData.foto.map(async (file) => {
          const formDataUpload = new FormData();
          formDataUpload.append("file", file.file || file);
          const uploadResponse = await axios.post(
            `${API_ESA_OFFICE}upload`,
            formDataUpload,
            {
              headers: { "Content-Type": "multipart/form-data" },
            }
          );
          return (
            uploadResponse.data.filePath ||
            `/uploads/${uploadResponse.data.filename}`
          );
        });
        fotoUrls = await Promise.all(uploadPromises);
      }

      // 2. Merge foto existing (yang tidak dihapus) dengan foto baru
      const remainingExistingFotos = existingFotos.filter(
        (url) => !removedFotoUrls.includes(url)
      );
      let finalFotoUrls = [...remainingExistingFotos, ...fotoUrls];

      // Jika tidak ada foto sama sekali, gunakan null
      if (finalFotoUrls.length === 0) {
        finalFotoUrls = null;
      }

      // 3. Tentukan dokumen final (boleh path upload atau URL eksternal)
      let finalDokumen = existingDokumen;
      if (removedExistingDokumen) {
        finalDokumen = null;
      }

      // Upload file dokumen jika ada
      if (formData.dokumenFile) {
        const fileObj = formData.dokumenFile.file || formData.dokumenFile;
        const formDataUpload = new FormData();
        formDataUpload.append("file", fileObj);
        const uploadResponse = await axios.post(
          `${API_ESA_OFFICE}upload`,
          formDataUpload,
          {
            headers: { "Content-Type": "multipart/form-data" },
          }
        );
        finalDokumen =
          uploadResponse.data.filePath ||
          `/uploads/${uploadResponse.data.filename}`;
      } else if (trimmedDokUrl) {
        // Jika user mengisi URL manual (misal Google Drive)
        finalDokumen = trimmedDokUrl;
      }

      // 4. Update detail event
      await axios.put(`${API_ESA_OFFICE}events/detail/${editingId}`, {
        notulen: formData.notulen || null,
        foto: finalFotoUrls,
        status: formData.status,
        dokumen: finalDokumen || null,
      });

      swal({
        title: "Berhasil!",
        text: "Laporan event berhasil disimpan",
        icon: "success",
        button: "OK",
      });

      handleCloseDialog();
      fetchDetailEvents();
    } catch (error) {
      console.error("Error saving laporan:", error);
      swal({
        title: "Error!",
        text: error.response?.data?.message || "Gagal menyimpan laporan",
        icon: "error",
        button: "OK",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusEventLabel = (status) => {
    switch (status) {
      case "satu_kali":
        return "Satu Kali";
      case "rutin":
        return "Rutin";
      case "evaluasi":
        return "Evaluasi";
      case "asistensi":
        return "Asistensi";
      default:
        return status;
    }
  };

  const getStatusEventColor = (status) => {
    switch (status) {
      case "satu_kali":
        return "info";
      case "rutin":
        return "warning";
      case "evaluasi":
        return "success";
      case "asistensi":
        return "secondary";
      default:
        return "default";
    }
  };

  // Gunakan useMemo untuk columns agar reference tetap stabil
  // Ini mencegah DataTable reset pagination saat re-render
  const columns = useMemo(
    () => [
      { Header: "No", accessor: "no", width: "5%" },
      {
        Header: "Detail Kegiatan",
        accessor: "detail_kegiatan",
        width: "200px",
        Cell: ({ row, value }) => {
          const statusDetailEvent = row.original.status;
          const hasLaporan =
            (row.original.notulen && row.original.notulen.trim() !== "") ||
            (row.original.foto &&
              Array.isArray(row.original.foto) &&
              row.original.foto.length > 0);
          const hasPeserta = row.original.jumlah_peserta > 0;

          return (
            <MDBox
              display="flex"
              alignItems="flex-start"
              gap={1}
              sx={{
                whiteSpace: "normal",
                wordWrap: "break-word",
                width: "200px",
                py: 1,
              }}
            >
              <MDTypography variant="caption" color="text" sx={{ flex: 1 }}>
                {value || "-"}
              </MDTypography>
              {statusDetailEvent === "selesai" && (
                <CheckCircleIcon
                  sx={{
                    color: "success.main",
                    fontSize: 36,
                    flexShrink: 0,
                  }}
                />
              )}
              {statusDetailEvent === "proses" && (
                <AccessTimeIcon
                  sx={{
                    color: "warning.main",
                    fontSize: 36,
                    flexShrink: 0,
                  }}
                />
              )}
            </MDBox>
          );
        },
      },
      {
        Header: "Dinas Peserta",
        accessor: "dinas_peserta",
        width: "15%",
        Cell: ({ row }) => {
          const isEvaluasiOrAsistensi =
            row.original.status_event === "evaluasi" ||
            row.original.status_event === "asistensi";

          if (!isEvaluasiOrAsistensi) {
            return (
              <MDTypography variant="caption" color="text.secondary">
                -
              </MDTypography>
            );
          }

          return (
            <MDBox
              sx={{
                whiteSpace: "normal",
                wordWrap: "break-word",
                maxWidth: "200px",
                py: 1,
              }}
            >
              <MDTypography variant="caption" color="text">
                {row.original.dinas_peserta_nama || "-"}
              </MDTypography>
            </MDBox>
          );
        },
      },
      {
        Header: "Waktu",
        accessor: "waktu",
        width: "15%",
        Cell: ({ row }) => (
          <MDBox>
            <MDTypography variant="caption" color="text" display="block">
              Mulai: {formatDateTime(row.original.waktu_mulai)}
            </MDTypography>
            <MDTypography variant="caption" color="text" display="block">
              Selesai: {formatDateTime(row.original.waktu_finish)}
            </MDTypography>
          </MDBox>
        ),
      },
      {
        Header: "Peserta",
        accessor: "jumlah_peserta",
        width: "10%",
        Cell: ({ row, value }) => {
          const jumlahPeserta = value || 0;
          return (
            <Button
              variant="outlined"
              size="small"
              startIcon={<PeopleIcon />}
              onClick={() =>
                handleOpenPesertaDialog(row.original.id, jumlahPeserta)
              }
              disabled={jumlahPeserta === 0}
              sx={{
                textTransform: "none",
                color: "info.main",
              }}
            >
              Lihat ({jumlahPeserta})
            </Button>
          );
        },
      },
      {
        Header: "Pembuat",
        accessor: "nama_pembuat",
        width: "15%",
        Cell: ({ row }) => (
          <MDBox display="flex" alignItems="center" gap={1}>
            {row.original.uid_RSS_pembuat ? (
              <img
                src={`https://smartdh.rotendaokab.go.id/uploads/${row.original.uid_RSS_pembuat}.jpg`}
                alt={row.original.nama_pembuat || "User"}
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  objectFit: "cover",
                }}
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
            ) : (
              <MDBox
                display="flex"
                alignItems="center"
                justifyContent="center"
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  backgroundColor: "grey.200",
                  color: "grey.500",
                }}
              >
                <PersonIcon sx={{ fontSize: 20 }} />
              </MDBox>
            )}
            <MDTypography variant="caption" color="text">
              {row.original.nama_pembuat || "-"}
            </MDTypography>
          </MDBox>
        ),
      },
      {
        Header: "Status",
        accessor: "status",
        width: "8%",
        Cell: ({ value }) => {
          if (!value) {
            return (
              <MDTypography variant="caption" color="text.secondary">
                -
              </MDTypography>
            );
          }
          const label = value === "selesai" ? "Selesai" : "Proses";
          const color = value === "selesai" ? "success" : "warning";
          return (
            <Chip label={label} color={color} size="small" variant="outlined" />
          );
        },
      },
      {
        Header: "Dokumen",
        accessor: "dokumen",
        width: "10%",
        Cell: ({ value }) => {
          if (!value) {
            return (
              <MDTypography variant="caption" color="text.secondary">
                -
              </MDTypography>
            );
          }
          const isExternal = /^https?:\/\//i.test(value);
          const tooltipTitle = isExternal
            ? "Buka Dokumen (Link Eksternal)"
            : "Buka Dokumen (File Upload)";
          return (
            <Tooltip title={tooltipTitle}>
              <IconButton
                color="primary"
                size="small"
                onClick={() => handleOpenDokumen(value)}
              >
                {isExternal ? (
                  <LinkIcon fontSize="small" />
                ) : (
                  <PictureAsPdfIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
          );
        },
      },
      {
        Header: "Foto",
        accessor: "foto",
        width: "8%",
        Cell: ({ row }) => {
          const hasFotos =
            row.original.foto &&
            Array.isArray(row.original.foto) &&
            row.original.foto.length > 0;
          if (!hasFotos) {
            return (
              <MDTypography variant="caption" color="text.secondary">
                -
              </MDTypography>
            );
          }
          return (
            <Tooltip title="Lihat Foto Kegiatan">
              <IconButton
                color="primary"
                size="small"
                onClick={() => handleOpenFotoDialog(row.original)}
              >
                <PhotoLibraryIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          );
        },
      },
      {
        Header: "Lapor",
        accessor: "lapor",
        width: "10%",
        Cell: ({ row }) => {
          // Pastikan userId dan id_pembuat ada dan valid
          const currentUserId = userId ? String(userId).trim() : null;
          const pembuatId = row.original.id_pembuat
            ? String(row.original.id_pembuat).trim()
            : null;

          const isPembuat =
            currentUserId && pembuatId && currentUserId === pembuatId;

          const hasLaporan =
            (row.original.notulen && row.original.notulen.trim() !== "") ||
            (row.original.foto &&
              Array.isArray(row.original.foto) &&
              row.original.foto.length > 0);

          return (
            <MDBox display="flex" gap={1}>
              {hasLaporan && (
                <Tooltip title="Lihat Laporan">
                  <IconButton
                    color="success"
                    size="small"
                    onClick={() => handleOpenViewDialog(row.original)}
                  >
                    <VisibilityIcon />
                  </IconButton>
                </Tooltip>
              )}
              {isPembuat && (
                <Tooltip title={hasLaporan ? "Edit Laporan" : "Buat Laporan"}>
                  <IconButton
                    color="info"
                    size="small"
                    onClick={() => handleOpenDialog(row.original)}
                  >
                    <EditIcon />
                  </IconButton>
                </Tooltip>
              )}
            </MDBox>
          );
        },
      },
      {
        Header: "Aksi",
        accessor: "aksi",
        width: "10%",
        Cell: ({ row }) => {
          // Pastikan userId dan id_pembuat ada dan valid
          const currentUserId = userId ? String(userId).trim() : null;
          const pembuatId = row.original.id_pembuat
            ? String(row.original.id_pembuat).trim()
            : null;

          const isPembuat =
            currentUserId && pembuatId && currentUserId === pembuatId;

          if (!isPembuat) {
            return null;
          }

          // Cek apakah bisa dihapus (tidak ada peserta dan belum lewat waktu selesai)
          const jumlahPeserta = row.original.jumlah_peserta || 0;
          const waktuFinish = row.original.waktu_finish
            ? new Date(row.original.waktu_finish)
            : null;
          const sekarang = new Date();
          const sudahLewatWaktu = waktuFinish && sekarang > waktuFinish;
          const bisaDihapus = jumlahPeserta === 0 && !sudahLewatWaktu;

          return (
            <MDBox display="flex" gap={1}>
              <Tooltip title="Edit Detail Event">
                <IconButton
                  color="info"
                  size="small"
                  onClick={() => handleOpenEditDetailDialog(row.original)}
                >
                  <EditIcon />
                </IconButton>
              </Tooltip>
              {bisaDihapus && (
                <Tooltip title="Hapus Detail Event">
                  <IconButton
                    color="error"
                    size="small"
                    onClick={() => handleDeleteDetailEvent(row.original)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              )}
            </MDBox>
          );
        },
      },
      {
        Header: "Tempat",
        accessor: "tempat",
        width: "200px",
        Cell: ({ value }) => (
          <MDBox
            sx={{
              whiteSpace: "normal",
              wordWrap: "break-word",
              width: "200px",
              py: 1,
            }}
          >
            <MDTypography variant="caption" color="text">
              {value || "-"}
            </MDTypography>
          </MDBox>
        ),
      },
    ],
    [userId]
  );

  // Kelompokkan detail events berdasarkan event
  const groupedEvents = useMemo(() => {
    const grouped = {};
    detailEventsList.forEach((detailEvent) => {
      const eventId = detailEvent.id_events;
      if (!grouped[eventId]) {
        grouped[eventId] = {
          id_events: eventId,
          kegiatan: detailEvent.kegiatan || "-",
          status_event: detailEvent.status_event || "-",
          nama_dinas: detailEvent.nama_dinas,
          nama_bidang: detailEvent.nama_bidang,
          // Gunakan data pembuat event, bukan pembuat detail event
          uid_RSS_pembuat: detailEvent.event_uid_RSS_pembuat || null,
          nama_pembuat: detailEvent.event_nama_pembuat || null,
          event_id_pembuat: detailEvent.event_id_pembuat || null,
          detailEvents: [],
        };
      }
      grouped[eventId].detailEvents.push(detailEvent);
    });

    // Hitung jumlah detail events yang sudah memiliki laporan untuk setiap group
    const groups = Object.values(grouped).map((group) => {
      const jumlahLaporan = group.detailEvents.filter((de) => {
        const hasNotulen = de.notulen && de.notulen.trim() !== "";
        const hasFoto = de.foto && Array.isArray(de.foto) && de.foto.length > 0;
        return hasNotulen || hasFoto;
      }).length;

      // Hitung jumlah detail events yang statusnya "selesai"
      const jumlahDetailEventsSelesai = group.detailEvents.filter(
        (de) => de.status === "selesai"
      ).length;

      // Ambil waktu_mulai terbaru dari detail events untuk sorting
      const latestWaktuMulai =
        group.detailEvents
          .map((de) =>
            de.waktu_mulai ? new Date(de.waktu_mulai).getTime() : 0
          )
          .sort((a, b) => b - a)[0] || 0;

      return {
        ...group,
        jumlah_detail_events: group.detailEvents.length,
        jumlah_laporan: jumlahLaporan,
        jumlah_detail_events_selesai: jumlahDetailEventsSelesai,
        latestWaktuMulai: latestWaktuMulai,
      };
    });

    // Urutkan dari terbaru ke terlama berdasarkan waktu_mulai terbaru
    return groups.sort((a, b) => b.latestWaktuMulai - a.latestWaktuMulai);
  }, [detailEventsList]);

  // Filter groupedEvents berdasarkan search query
  const filteredGroupedEvents = useMemo(() => {
    if (!searchQuery.trim()) {
      return groupedEvents;
    }
    const query = searchQuery.toLowerCase().trim();
    return groupedEvents.filter((group) =>
      group.kegiatan?.toLowerCase().includes(query)
    );
  }, [groupedEvents, searchQuery]);

  // Fetch statistics untuk setiap event
  useEffect(() => {
    const fetchStatistics = async () => {
      const stats = {};
      for (const group of groupedEvents) {
        try {
          const response = await axios.get(
            `${API_ESA_OFFICE}events/${group.id_events}/statistics`
          );
          stats[group.id_events] = response.data;
        } catch (error) {
          console.error(
            `Error fetching statistics for event ${group.id_events}:`,
            error
          );
          // Set default values jika error
          stats[group.id_events] = {
            total_peserta: 0,
            jumlah_dinas: 0,
            total_target_dinas: 0,
            jumlah_dinas_sudah_hadir: 0,
            jumlah_dinas_belum_hadir: 0,
            progress_persentase: 0,
            dinas_sudah_hadir: [],
            dinas_belum_hadir: [],
            progress_md: "",
          };
        }
      }
      setEventStatistics(stats);
    };

    if (groupedEvents.length > 0) {
      fetchStatistics();
    }
  }, [groupedEvents]);

  // Gunakan useMemo untuk mencegah re-create rows setiap render
  // Ini akan menjaga pagination state tetap stabil
  const rows = useMemo(() => {
    return detailEventsList.map((detailEvent, index) => ({
      id: detailEvent.id,
      id_events: detailEvent.id_events,
      no: index + 1,
      kegiatan: detailEvent.kegiatan || "-",
      status_event: detailEvent.status_event || "-",
      detail_kegiatan: detailEvent.detail_kegiatan || "-",
      waktu_mulai: detailEvent.waktu_mulai,
      waktu_finish: detailEvent.waktu_finish,
      tempat: detailEvent.tempat || "-",
      jumlah_peserta: detailEvent.jumlah_peserta || 0,
      nama_pembuat: detailEvent.nama_pembuat || "-",
      id_pembuat: detailEvent.id_pembuat,
      uid_RSS_pembuat: detailEvent.uid_RSS_pembuat,
      notulen: detailEvent.notulen,
      foto: detailEvent.foto,
      nama_dinas: detailEvent.nama_dinas,
      nama_bidang: detailEvent.nama_bidang,
      status: detailEvent.status || null,
      dokumen: detailEvent.dokumen || null,
    }));
  }, [detailEventsList]);

  // Mapping table data untuk setiap group - dibuat di level komponen untuk stabilitas pagination
  const groupTableDataMap = useMemo(() => {
    const map = {};
    groupedEvents.forEach((group) => {
      map[group.id_events] = {
        columns: columns,
        rows: group.detailEvents.map((detailEvent, index) => ({
          id: detailEvent.id,
          id_events: detailEvent.id_events,
          no: index + 1,
          kegiatan: detailEvent.kegiatan || "-",
          status_event: detailEvent.status_event || "-",
          detail_kegiatan: detailEvent.detail_kegiatan || "-",
          waktu_mulai: detailEvent.waktu_mulai,
          waktu_finish: detailEvent.waktu_finish,
          tempat: detailEvent.tempat || "-",
          jumlah_peserta: detailEvent.jumlah_peserta || 0,
          nama_pembuat: detailEvent.nama_pembuat || "-",
          id_pembuat: detailEvent.id_pembuat,
          uid_RSS_pembuat: detailEvent.uid_RSS_pembuat,
          notulen: detailEvent.notulen,
          foto: detailEvent.foto,
          nama_dinas: detailEvent.nama_dinas,
          nama_bidang: detailEvent.nama_bidang,
          dinas_peserta_nama: detailEvent.dinas_peserta_nama,
          dinas_id: detailEvent.dinas_id,
          status: detailEvent.status || null,
          dokumen: detailEvent.dokumen || null,
        })),
      };
    });
    return map;
  }, [groupedEvents, columns]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <MDTypography variant="h6" color="white">
                  Event Saya
                </MDTypography>
              </MDBox>
              <MDBox pt={4} pb={3} px={3}>
                {/* Filter Tahun dan Bulan */}
                <Grid container spacing={2} mb={3}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Autocomplete
                      options={(() => {
                        const currentYear = new Date().getFullYear();
                        const years = [];
                        // Mulai dari 2025 sampai current tahun
                        for (let i = 2025; i <= currentYear; i++) {
                          years.push({ label: i.toString(), value: i });
                        }
                        // Urutkan dari terbaru ke terlama (DESC)
                        return years.reverse();
                      })()}
                      value={
                        tahun ? { label: tahun.toString(), value: tahun } : null
                      }
                      onChange={(event, newValue) => {
                        if (newValue) {
                          setTahun(newValue.value);
                        }
                      }}
                      renderInput={(params) => (
                        <MDInput
                          {...params}
                          label="Tahun"
                          variant="standard"
                          fullWidth
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Autocomplete
                      options={[
                        { label: "Semua Bulan", value: "semua" },
                        { label: "Januari", value: 1 },
                        { label: "Februari", value: 2 },
                        { label: "Maret", value: 3 },
                        { label: "April", value: 4 },
                        { label: "Mei", value: 5 },
                        { label: "Juni", value: 6 },
                        { label: "Juli", value: 7 },
                        { label: "Agustus", value: 8 },
                        { label: "September", value: 9 },
                        { label: "Oktober", value: 10 },
                        { label: "November", value: 11 },
                        { label: "Desember", value: 12 },
                      ]}
                      value={
                        bulan
                          ? bulan === "semua"
                            ? { label: "Semua Bulan", value: "semua" }
                            : {
                                label: [
                                  "Januari",
                                  "Februari",
                                  "Maret",
                                  "April",
                                  "Mei",
                                  "Juni",
                                  "Juli",
                                  "Agustus",
                                  "September",
                                  "Oktober",
                                  "November",
                                  "Desember",
                                ][bulan - 1],
                                value: bulan,
                              }
                          : null
                      }
                      onChange={(event, newValue) => {
                        if (newValue) {
                          setBulan(
                            newValue.value === "semua" ? null : newValue.value
                          );
                        }
                      }}
                      renderInput={(params) => (
                        <MDInput
                          {...params}
                          label="Bulan"
                          variant="standard"
                          fullWidth
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={6}>
                    <MDInput
                      label="Cari Nama Event"
                      variant="standard"
                      fullWidth
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <SearchIcon sx={{ color: "text.secondary", mr: 1 }} />
                        ),
                      }}
                    />
                  </Grid>
                </Grid>
                {loading ? (
                  <MDBox
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    minHeight="200px"
                  >
                    <CircularProgress />
                    <MDTypography variant="body2" color="text" ml={2}>
                      Memuat data...
                    </MDTypography>
                  </MDBox>
                ) : isMobile ? (
                  <MDBox display="flex" flexDirection="column" gap={1}>
                    {filteredGroupedEvents.length === 0 ? (
                      <MDBox
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                        py={4}
                      >
                        <MDTypography variant="h6" color="text.secondary">
                          {searchQuery.trim()
                            ? "Tidak ada event yang sesuai dengan pencarian"
                            : "Tidak ada data event"}
                        </MDTypography>
                        <MDTypography variant="body2" color="text.secondary">
                          {searchQuery.trim()
                            ? "Coba ubah kata kunci pencarian"
                            : "Belum ada event yang Anda buat"}
                        </MDTypography>
                      </MDBox>
                    ) : (
                      filteredGroupedEvents.map((group) => (
                        <Accordion key={group.id_events} defaultExpanded>
                          <AccordionSummary
                            expandIcon={
                              <ExpandMoreIcon
                                sx={{
                                  color: "success.main",
                                  fontWeight: "bold",
                                }}
                              />
                            }
                            sx={{
                              backgroundColor: "grey.100",
                              "&:hover": {
                                backgroundColor: "grey.200",
                              },
                            }}
                          >
                            <MDBox
                              display="flex"
                              flexDirection="column"
                              gap={1}
                              width="100%"
                            >
                              <MDBox
                                display="flex"
                                alignItems="center"
                                gap={1}
                                flexWrap="wrap"
                                width="100%"
                              >
                                <MDTypography variant="h6" fontWeight="bold">
                                  {group.kegiatan}
                                </MDTypography>
                                <Chip
                                  label={getStatusEventLabel(
                                    group.status_event
                                  )}
                                  color={getStatusEventColor(
                                    group.status_event
                                  )}
                                  size="small"
                                  variant="filled"
                                />
                                {group.uid_RSS_pembuat ? (
                                  <img
                                    src={`https://smartdh.rotendaokab.go.id/uploads/${group.uid_RSS_pembuat}.jpg`}
                                    alt={group.nama_pembuat || "Pembuat Event"}
                                    style={{
                                      width: 32,
                                      height: 32,
                                      borderRadius: "50%",
                                      objectFit: "cover",
                                      marginLeft: "8px",
                                    }}
                                    onError={(e) => {
                                      e.target.style.display = "none";
                                    }}
                                  />
                                ) : (
                                  <MDBox
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    sx={{
                                      width: 32,
                                      height: 32,
                                      borderRadius: "50%",
                                      backgroundColor: "grey.200",
                                      color: "grey.500",
                                      marginLeft: "8px",
                                    }}
                                  >
                                    <PersonIcon sx={{ fontSize: 20 }} />
                                  </MDBox>
                                )}
                                {group.nama_pembuat && (
                                  <MDTypography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ ml: 0.5 }}
                                  >
                                    {group.nama_pembuat}
                                  </MDTypography>
                                )}
                                <MDTypography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ ml: "auto" }}
                                >
                                  {group.jumlah_detail_events} detail
                                </MDTypography>
                              </MDBox>
                              <MDBox
                                display="flex"
                                alignItems="center"
                                gap={2}
                                flexWrap="wrap"
                                mt={1}
                              >
                                <MDTypography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  <strong>Detail Events:</strong>{" "}
                                  {group.jumlah_detail_events}
                                </MDTypography>
                                <MDTypography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  <strong>Sudah Laporan:</strong>{" "}
                                  {group.jumlah_laporan} /{" "}
                                  {group.jumlah_detail_events}
                                </MDTypography>
                              </MDBox>
                              <MDBox
                                display="flex"
                                flexDirection="column"
                                gap={0.5}
                                mt={1}
                                sx={{ minWidth: "200px", flex: 1 }}
                              >
                                <MDBox
                                  display="flex"
                                  justifyContent="space-between"
                                  alignItems="center"
                                >
                                  <MDTypography
                                    variant="caption"
                                    color="text.secondary"
                                    fontWeight="medium"
                                  >
                                    Progress Selesai:
                                  </MDTypography>
                                  <MDTypography
                                    variant="caption"
                                    color="text.secondary"
                                    fontWeight="bold"
                                  >
                                    {group.jumlah_detail_events_selesai || 0} /{" "}
                                    {group.jumlah_detail_events}
                                  </MDTypography>
                                </MDBox>
                                <MDProgress
                                  value={
                                    group.jumlah_detail_events > 0
                                      ? Math.round(
                                          (group.jumlah_detail_events_selesai /
                                            group.jumlah_detail_events) *
                                            100
                                        )
                                      : 0
                                  }
                                  color="success"
                                  variant="gradient"
                                  sx={{
                                    height: 8,
                                    borderRadius: 4,
                                  }}
                                />
                              </MDBox>
                              {eventStatistics[group.id_events] && (
                                <MDBox
                                  display="flex"
                                  alignItems="center"
                                  gap={2}
                                  flexWrap="wrap"
                                  mt={1}
                                >
                                  <MDTypography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    <strong>Total Peserta:</strong>{" "}
                                    {
                                      eventStatistics[group.id_events]
                                        .total_peserta
                                    }
                                  </MDTypography>
                                  {(group.status_event === "evaluasi" ||
                                    group.status_event === "asistensi") && (
                                    <>
                                      <MDTypography
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        <strong>Jumlah Dinas:</strong>{" "}
                                        {
                                          eventStatistics[group.id_events]
                                            .jumlah_dinas
                                        }
                                      </MDTypography>
                                      <MDTypography
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        <strong>Dinas Hadir:</strong>{" "}
                                        {
                                          eventStatistics[group.id_events]
                                            .jumlah_dinas_sudah_hadir
                                        }
                                        /
                                        {
                                          eventStatistics[group.id_events]
                                            .jumlah_dinas
                                        }
                                      </MDTypography>
                                    </>
                                  )}
                                </MDBox>
                              )}
                            </MDBox>
                          </AccordionSummary>
                          <AccordionDetails>
                            <MDBox
                              display="flex"
                              flexDirection="column"
                              gap={1}
                            >
                              {group.detailEvents.map((detailEvent) => {
                                const hasLaporan =
                                  (detailEvent.notulen &&
                                    detailEvent.notulen.trim() !== "") ||
                                  (detailEvent.foto &&
                                    Array.isArray(detailEvent.foto) &&
                                    detailEvent.foto.length > 0);
                                return (
                                  <Card key={detailEvent.id}>
                                    <CardContent>
                                      <MDBox>
                                        <MDTypography
                                          variant="h6"
                                          fontWeight="bold"
                                        >
                                          {detailEvent.kegiatan || "-"}
                                        </MDTypography>
                                        <MDBox
                                          mt={1}
                                          display="flex"
                                          gap={1}
                                          alignItems="center"
                                          flexWrap="wrap"
                                        >
                                          <MDTypography
                                            variant="caption"
                                            color="text.secondary"
                                          >
                                            Status:
                                          </MDTypography>
                                          <Chip
                                            label={getStatusEventLabel(
                                              detailEvent.status_event
                                            )}
                                            color={getStatusEventColor(
                                              detailEvent.status_event
                                            )}
                                            size="small"
                                            variant="filled"
                                          />
                                        </MDBox>
                                        {detailEvent.detail_kegiatan && (
                                          <MDBox mt={1}>
                                            <MDTypography
                                              variant="caption"
                                              color="text.secondary"
                                            >
                                              Detail Kegiatan:
                                            </MDTypography>
                                            <MDTypography
                                              variant="body2"
                                              color="text"
                                            >
                                              {detailEvent.detail_kegiatan}
                                            </MDTypography>
                                          </MDBox>
                                        )}
                                        <MDBox mt={1}>
                                          <MDTypography
                                            variant="caption"
                                            color="text.secondary"
                                          >
                                            Waktu:
                                          </MDTypography>
                                          {detailEvent.waktu_mulai && (
                                            <MDTypography
                                              variant="body2"
                                              color="text"
                                            >
                                              Mulai:{" "}
                                              {formatDateTime(
                                                detailEvent.waktu_mulai
                                              )}
                                            </MDTypography>
                                          )}
                                          {detailEvent.waktu_finish && (
                                            <MDTypography
                                              variant="body2"
                                              color="text"
                                            >
                                              Selesai:{" "}
                                              {formatDateTime(
                                                detailEvent.waktu_finish
                                              )}
                                            </MDTypography>
                                          )}
                                        </MDBox>
                                        {detailEvent.tempat && (
                                          <MDBox mt={1}>
                                            <MDTypography
                                              variant="caption"
                                              color="text.secondary"
                                            >
                                              Tempat:
                                            </MDTypography>
                                            <MDTypography
                                              variant="body2"
                                              color="text"
                                            >
                                              {detailEvent.tempat}
                                            </MDTypography>
                                          </MDBox>
                                        )}
                                        {detailEvent.jumlah_peserta !==
                                          undefined && (
                                          <MDBox mt={1}>
                                            <MDTypography
                                              variant="caption"
                                              color="text.secondary"
                                            >
                                              Peserta:
                                            </MDTypography>
                                            <MDTypography
                                              variant="body2"
                                              color="text"
                                              fontWeight="medium"
                                            >
                                              {detailEvent.jumlah_peserta || 0}{" "}
                                              orang
                                            </MDTypography>
                                          </MDBox>
                                        )}
                                        <MDBox
                                          mt={2}
                                          display="flex"
                                          gap={1}
                                          flexWrap="wrap"
                                        >
                                          {hasLaporan && (
                                            <Tooltip title="Lihat Laporan">
                                              <IconButton
                                                color="success"
                                                size="small"
                                                onClick={() =>
                                                  handleOpenViewDialog(
                                                    detailEvent
                                                  )
                                                }
                                              >
                                                <VisibilityIcon fontSize="small" />
                                              </IconButton>
                                            </Tooltip>
                                          )}
                                          {(() => {
                                            // Pastikan userId dan id_pembuat ada dan valid
                                            const currentUserId = userId
                                              ? String(userId).trim()
                                              : null;
                                            const pembuatId =
                                              detailEvent.id_pembuat
                                                ? String(
                                                    detailEvent.id_pembuat
                                                  ).trim()
                                                : null;
                                            const isPembuat =
                                              currentUserId &&
                                              pembuatId &&
                                              currentUserId === pembuatId;

                                            return (
                                              isPembuat && (
                                                <>
                                                  <Tooltip
                                                    title={
                                                      hasLaporan
                                                        ? "Edit Laporan"
                                                        : "Buat Laporan"
                                                    }
                                                  >
                                                    <IconButton
                                                      color="info"
                                                      size="small"
                                                      onClick={() =>
                                                        handleOpenDialog(
                                                          detailEvent
                                                        )
                                                      }
                                                    >
                                                      <EditIcon fontSize="small" />
                                                    </IconButton>
                                                  </Tooltip>
                                                  <Tooltip title="Edit Detail Event">
                                                    <IconButton
                                                      color="info"
                                                      size="small"
                                                      onClick={() =>
                                                        handleOpenEditDetailDialog(
                                                          detailEvent
                                                        )
                                                      }
                                                    >
                                                      <EditIcon fontSize="small" />
                                                    </IconButton>
                                                  </Tooltip>
                                                  {(() => {
                                                    // Cek apakah bisa dihapus (tidak ada peserta dan belum lewat waktu selesai)
                                                    const jumlahPeserta =
                                                      detailEvent.jumlah_peserta ||
                                                      0;
                                                    const waktuFinish =
                                                      detailEvent.waktu_finish
                                                        ? new Date(
                                                            detailEvent.waktu_finish
                                                          )
                                                        : null;
                                                    const sekarang = new Date();
                                                    const sudahLewatWaktu =
                                                      waktuFinish &&
                                                      sekarang > waktuFinish;
                                                    const bisaDihapus =
                                                      jumlahPeserta === 0 &&
                                                      !sudahLewatWaktu;

                                                    return bisaDihapus ? (
                                                      <Tooltip title="Hapus Detail Event">
                                                        <IconButton
                                                          color="error"
                                                          size="small"
                                                          onClick={() =>
                                                            handleDeleteDetailEvent(
                                                              detailEvent
                                                            )
                                                          }
                                                        >
                                                          <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                      </Tooltip>
                                                    ) : null;
                                                  })()}
                                                </>
                                              )
                                            );
                                          })()}
                                        </MDBox>
                                      </MDBox>
                                    </CardContent>
                                  </Card>
                                );
                              })}
                            </MDBox>
                          </AccordionDetails>
                        </Accordion>
                      ))
                    )}
                  </MDBox>
                ) : (
                  <MDBox>
                    {filteredGroupedEvents.length === 0 ? (
                      <MDBox
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                        py={4}
                      >
                        <MDTypography variant="h6" color="text.secondary">
                          {searchQuery.trim()
                            ? "Tidak ada event yang sesuai dengan pencarian"
                            : "Tidak ada data event"}
                        </MDTypography>
                        <MDTypography variant="body2" color="text.secondary">
                          {searchQuery.trim()
                            ? "Coba ubah kata kunci pencarian"
                            : "Belum ada event yang Anda buat"}
                        </MDTypography>
                      </MDBox>
                    ) : (
                      filteredGroupedEvents.map((group, groupIndex) => (
                        <Accordion
                          key={group.id_events}
                          defaultExpanded={groupIndex === 0}
                        >
                          <AccordionSummary
                            expandIcon={
                              <ExpandMoreIcon
                                sx={{
                                  color: "success.main",
                                  fontWeight: "bold",
                                }}
                              />
                            }
                            sx={{
                              backgroundColor: "grey.100",
                              "&:hover": {
                                backgroundColor: "grey.200",
                              },
                            }}
                          >
                            <MDBox
                              display="flex"
                              flexDirection="column"
                              gap={1}
                              width="100%"
                            >
                              <MDBox
                                display="flex"
                                alignItems="center"
                                gap={2}
                                width="100%"
                              >
                                <MDTypography variant="h6" fontWeight="bold">
                                  {group.kegiatan}
                                </MDTypography>
                                <Chip
                                  label={getStatusEventLabel(
                                    group.status_event
                                  )}
                                  color={getStatusEventColor(
                                    group.status_event
                                  )}
                                  size="small"
                                  variant="filled"
                                />
                                {group.uid_RSS_pembuat ? (
                                  <img
                                    src={`https://smartdh.rotendaokab.go.id/uploads/${group.uid_RSS_pembuat}.jpg`}
                                    alt={group.nama_pembuat || "Pembuat Event"}
                                    style={{
                                      width: 32,
                                      height: 32,
                                      borderRadius: "50%",
                                      objectFit: "cover",
                                      marginLeft: "8px",
                                    }}
                                    onError={(e) => {
                                      e.target.style.display = "none";
                                    }}
                                  />
                                ) : (
                                  <MDBox
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    sx={{
                                      width: 32,
                                      height: 32,
                                      borderRadius: "50%",
                                      backgroundColor: "grey.200",
                                      color: "grey.500",
                                      marginLeft: "8px",
                                    }}
                                  >
                                    <PersonIcon sx={{ fontSize: 20 }} />
                                  </MDBox>
                                )}
                                {group.nama_pembuat && (
                                  <MDTypography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ ml: 0.5 }}
                                  >
                                    {group.nama_pembuat}
                                  </MDTypography>
                                )}
                                <MDTypography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ ml: "auto" }}
                                >
                                  {group.jumlah_detail_events} detail event
                                </MDTypography>
                              </MDBox>
                              <MDBox
                                display="flex"
                                alignItems="center"
                                gap={2}
                                flexWrap="wrap"
                                mt={1}
                              >
                                <MDTypography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  <strong>Detail Events:</strong>{" "}
                                  {group.jumlah_detail_events}
                                </MDTypography>
                                <MDTypography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  <strong>Sudah Laporan:</strong>{" "}
                                  {group.jumlah_laporan} /{" "}
                                  {group.jumlah_detail_events}
                                </MDTypography>
                              </MDBox>
                              <MDBox
                                display="flex"
                                flexDirection="column"
                                gap={0.5}
                                mt={1}
                                sx={{ minWidth: "200px", flex: 1 }}
                              >
                                <MDBox
                                  display="flex"
                                  justifyContent="space-between"
                                  alignItems="center"
                                >
                                  <MDTypography
                                    variant="caption"
                                    color="text.secondary"
                                    fontWeight="medium"
                                  >
                                    Progress Selesai:
                                  </MDTypography>
                                  <MDTypography
                                    variant="caption"
                                    color="text.secondary"
                                    fontWeight="bold"
                                  >
                                    {group.jumlah_detail_events_selesai || 0} /{" "}
                                    {group.jumlah_detail_events}
                                  </MDTypography>
                                </MDBox>
                                <MDProgress
                                  value={
                                    group.jumlah_detail_events > 0
                                      ? Math.round(
                                          (group.jumlah_detail_events_selesai /
                                            group.jumlah_detail_events) *
                                            100
                                        )
                                      : 0
                                  }
                                  color="success"
                                  variant="gradient"
                                  sx={{
                                    height: 8,
                                    borderRadius: 4,
                                  }}
                                />
                              </MDBox>
                              {eventStatistics[group.id_events] && (
                                <MDBox
                                  display="flex"
                                  alignItems="center"
                                  gap={2}
                                  flexWrap="wrap"
                                  mt={1}
                                >
                                  <MDTypography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    <strong>Total Peserta:</strong>{" "}
                                    {
                                      eventStatistics[group.id_events]
                                        .total_peserta
                                    }
                                  </MDTypography>
                                  {(group.status_event === "evaluasi" ||
                                    group.status_event === "asistensi") && (
                                    <>
                                      <MDTypography
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        <strong>Jumlah Dinas:</strong>{" "}
                                        {
                                          eventStatistics[group.id_events]
                                            .jumlah_dinas
                                        }
                                      </MDTypography>
                                      <MDTypography
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        <strong>Dinas Hadir:</strong>{" "}
                                        {
                                          eventStatistics[group.id_events]
                                            .jumlah_dinas_sudah_hadir
                                        }
                                        /
                                        {
                                          eventStatistics[group.id_events]
                                            .jumlah_dinas
                                        }
                                      </MDTypography>
                                    </>
                                  )}
                                </MDBox>
                              )}
                            </MDBox>
                          </AccordionSummary>
                          <AccordionDetails>
                            <DataTable
                              table={
                                groupTableDataMap[group.id_events] || {
                                  columns: columns,
                                  rows: [],
                                }
                              }
                              entriesPerPage={{
                                defaultValue: 10,
                                entries: [5, 10, 15, 20, 25],
                              }}
                              showTotalEntries={true}
                              isSorted={true}
                              noEndBorder
                            />
                          </AccordionDetails>
                        </Accordion>
                      ))
                    )}
                  </MDBox>
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* Dialog Form Laporan */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <MDBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <MDTypography variant="h6">Laporan Event Saya</MDTypography>
            <IconButton onClick={handleCloseDialog}>
              <CloseIcon />
            </IconButton>
          </MDBox>
        </DialogTitle>
        <DialogContent>
          <MDBox pt={2} pb={3} px={3}>
            {/* Status Pelaksanaan */}
            <MDBox mb={3}>
              <MDBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                <MDTypography
                  component="label"
                  variant="button"
                  fontWeight="regular"
                  color="text"
                >
                  Status Pelaksanaan <span style={{ color: "red" }}>*</span>
                </MDTypography>
              </MDBox>
              <Autocomplete
                options={[
                  { label: "Proses", value: "proses" },
                  { label: "Selesai", value: "selesai" },
                ]}
                value={
                  formData.status
                    ? {
                        label:
                          formData.status === "selesai" ? "Selesai" : "Proses",
                        value: formData.status,
                      }
                    : null
                }
                onChange={(event, newValue) =>
                  setFormData((prev) => ({
                    ...prev,
                    status: newValue ? newValue.value : "",
                  }))
                }
                renderInput={(params) => (
                  <MDInput
                    {...params}
                    placeholder="Pilih status pelaksanaan"
                    fullWidth
                    required
                  />
                )}
              />
            </MDBox>

            <MDBox mb={3}>
              <MDBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                <MDTypography
                  component="label"
                  variant="button"
                  fontWeight="regular"
                  color="text"
                >
                  Notulen
                </MDTypography>
              </MDBox>
              <MDEditor
                value={formData.notulen || ""}
                onChange={(val) =>
                  setFormData((prev) => ({
                    ...prev,
                    notulen: val,
                  }))
                }
              />
            </MDBox>
            {/* Dokumen Notulen */}
            <MDBox mb={3}>
              <MDBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                <MDTypography
                  component="label"
                  variant="button"
                  fontWeight="regular"
                  color="text"
                >
                  Dokumen Notulen (PDF)
                  <br />
                  <MDTypography
                    component="span"
                    variant="caption"
                    color="text.secondary"
                  >
                    Anda dapat mengupload file PDF atau mengisi URL eksternal
                    (misalnya Google Drive). Pilih salah satu.
                  </MDTypography>
                </MDTypography>
              </MDBox>

              {/* Dokumen yang sudah ada */}
              {existingDokumen && !removedExistingDokumen && (
                <MDBox mb={2} display="flex" alignItems="center" gap={1}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={
                      /^https?:\/\//i.test(existingDokumen) ? (
                        <LinkIcon />
                      ) : (
                        <PictureAsPdfIcon />
                      )
                    }
                    onClick={() => handleOpenDokumen(existingDokumen)}
                  >
                    Buka Dokumen Saat Ini
                  </Button>
                  <Button
                    variant="text"
                    color="error"
                    size="small"
                    onClick={() => {
                      setRemovedExistingDokumen(true);
                      setExistingDokumen(null);
                    }}
                  >
                    Hapus Dokumen
                  </Button>
                </MDBox>
              )}

              {/* Input URL dokumen */}
              <MDBox mb={2}>
                <MDInput
                  type="url"
                  fullWidth
                  value={formData.dokumenUrl}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      dokumenUrl: e.target.value,
                    }))
                  }
                  placeholder="URL dokumen (misalnya https://drive.google.com/...)"
                />
              </MDBox>

              {/* Upload file dokumen (PDF) */}
              <MDBox>
                <input
                  type="file"
                  accept="application/pdf,.pdf"
                  onChange={(e) => {
                    const file = e.target.files && e.target.files[0];
                    if (file) {
                      if (
                        file.type &&
                        file.type.toLowerCase() !== "application/pdf"
                      ) {
                        swal({
                          title: "Error",
                          text: "Format file tidak didukung. Hanya PDF yang diperbolehkan",
                          icon: "error",
                        });
                        e.target.value = "";
                        return;
                      }
                      // Batas ukuran 1 MB
                      const maxSizeBytes = 1 * 1024 * 1024;
                      if (file.size > maxSizeBytes) {
                        swal({
                          title: "Error",
                          text: "Ukuran file dokumen maksimal 1 MB",
                          icon: "error",
                        });
                        e.target.value = "";
                        return;
                      }
                    }
                    setFormData((prev) => ({
                      ...prev,
                      dokumenFile: file || null,
                    }));
                  }}
                  style={{ display: "block", marginTop: 8 }}
                />
                <MDTypography variant="caption" color="text.secondary">
                  Jika memilih file, pastikan berformat PDF dan ukuran maksimal
                  1 MB.
                </MDTypography>
              </MDBox>
            </MDBox>

            {/* Foto Kegiatan */}
            <MDBox>
              <MDBox mb={1}>
                <MDTypography
                  component="label"
                  variant="button"
                  fontWeight="regular"
                  color="text"
                >
                  Foto Kegiatan (Opsional)
                  <br />
                  <MDTypography
                    component="span"
                    variant="caption"
                    color="text.secondary"
                  >
                    Format: JPG, PNG | Maksimal: 3 file, 1.5 MB per file
                  </MDTypography>
                </MDTypography>
              </MDBox>

              {/* Preview Foto Existing */}
              {existingFotos.length > 0 && (
                <MDBox mb={3}>
                  <MDTypography
                    variant="caption"
                    fontWeight="medium"
                    color="text"
                    mb={1}
                  >
                    Foto yang sudah diupload:
                  </MDTypography>
                  <Grid container spacing={2}>
                    {existingFotos.map((fotoUrl, index) => (
                      <Grid item xs={6} sm={4} md={3} key={index}>
                        <Card
                          sx={{
                            position: "relative",
                            paddingTop: "100%",
                            overflow: "hidden",
                          }}
                        >
                          <img
                            src={
                              fotoUrl.startsWith("/")
                                ? `${API_ESA_OFFICE_UPLOAD}${fotoUrl}`
                                : `${API_ESA_OFFICE_UPLOAD}/${fotoUrl}`
                            }
                            alt={`Foto ${index + 1}`}
                            style={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                            onError={(e) => {
                              e.target.src =
                                "https://via.placeholder.com/300?text=Gambar+Tidak+Tersedia";
                            }}
                          />
                          <IconButton
                            onClick={() => handleRemoveExistingFoto(fotoUrl)}
                            sx={{
                              position: "absolute",
                              top: 4,
                              right: 4,
                              backgroundColor: "rgba(255, 255, 255, 0.9)",
                              "&:hover": {
                                backgroundColor: "rgba(255, 255, 255, 1)",
                              },
                              color: "error.main",
                              padding: "4px",
                            }}
                            size="small"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </MDBox>
              )}

              {useMemo(
                () => (
                  <MDDropzone
                    options={{
                      addRemoveLinks: true,
                      uploadMultiple: true,
                      maxFiles: 3,
                      maxFilesize: 1.5,
                      acceptedFiles: ".jpg,.jpeg,.png",
                      dictDefaultMessage:
                        "Klik atau seret foto ke sini untuk mengunggah",
                      dictMaxFilesExceeded: "Maksimal 3 file",
                      dictFileTooBig:
                        "Ukuran file melebihi 1.5 MB. Silakan pilih file yang lebih kecil",
                      dictInvalidFileType:
                        "Format file tidak didukung. Hanya JPG dan PNG yang diperbolehkan",
                    }}
                    onChange={handleDropzoneChange}
                    ref={dropzoneRef}
                  />
                ),
                []
              )}
            </MDBox>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={submitting}>
            Batal
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="info"
            disabled={submitting}
            startIcon={
              submitting ? <CircularProgress size={16} color="inherit" /> : null
            }
          >
            {submitting ? "Menyimpan..." : "Simpan"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog View Notulen dan Foto - PDF */}
      <Dialog
        open={openViewDialog}
        onClose={handleCloseViewDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <MDBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <MDTypography variant="h6">Lihat Laporan Event (PDF)</MDTypography>
            <IconButton onClick={handleCloseViewDialog}>
              <CloseIcon />
            </IconButton>
          </MDBox>
        </DialogTitle>
        <DialogContent>
          <MDBox pt={2} pb={3} px={3}>
            {pdfBlobUrl ? (
              <iframe
                src={pdfBlobUrl}
                width="100%"
                height="600px"
                title="PDF Viewer"
                style={{ border: "none" }}
                onError={(e) => {
                  console.error("Error loading PDF:", e);
                  swal({
                    icon: "error",
                    title: "Oops...",
                    text: "Gagal memuat PDF",
                    button: "OK",
                  });
                }}
              />
            ) : (
              <MDBox
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight="200px"
              >
                <CircularProgress />
                <MDTypography variant="body2" color="text" ml={2}>
                  Membuat PDF...
                </MDTypography>
              </MDBox>
            )}
          </MDBox>
        </DialogContent>
        <DialogActions>
          {pdfBlobUrl && (
            <Button
              onClick={() => {
                const link = document.createElement("a");
                link.href = pdfBlobUrl;
                link.download = `Laporan_Event_${
                  viewingDetailEvent?.kegiatan || "Event"
                }_${new Date().getTime()}.pdf`;
                link.click();
              }}
              variant="outlined"
              color="primary"
              sx={{ color: "black", borderColor: "black" }}
            >
              Download PDF
            </Button>
          )}
          <Button
            onClick={handleCloseViewDialog}
            variant="contained"
            color="primary"
          >
            Tutup
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Foto Kegiatan */}
      <Dialog
        open={openFotoDialog}
        onClose={handleCloseFotoDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <MDBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <MDTypography variant="h6">Foto Kegiatan</MDTypography>
            <IconButton onClick={handleCloseFotoDialog}>
              <CloseIcon />
            </IconButton>
          </MDBox>
        </DialogTitle>
        <DialogContent>
          <MDBox pt={2} pb={3} px={3}>
            {viewingFotos && viewingFotos.length > 0 ? (
              <Grid container spacing={2}>
                {viewingFotos.map((fotoUrl, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <Card
                      sx={{
                        position: "relative",
                        paddingTop: "75%",
                        overflow: "hidden",
                      }}
                    >
                      <img
                        src={
                          fotoUrl.startsWith("/")
                            ? `${API_ESA_OFFICE_UPLOAD}${fotoUrl}`
                            : `${API_ESA_OFFICE_UPLOAD}/${fotoUrl}`
                        }
                        alt={`Foto ${index + 1}`}
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                        onError={(e) => {
                          e.target.src =
                            "https://via.placeholder.com/300?text=Gambar+Tidak+Tersedia";
                        }}
                      />
                    </Card>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <MDTypography variant="body2" color="text.secondary">
                Tidak ada foto untuk ditampilkan
              </MDTypography>
            )}
          </MDBox>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseFotoDialog}
            variant="contained"
            color="primary"
          >
            Tutup
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Progress MD */}
      <Dialog
        open={openProgressDialog}
        onClose={() => {
          setOpenProgressDialog(false);
          setViewingProgressMD("");
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <MDBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <MDTypography variant="h6">Progress Kehadiran Dinas</MDTypography>
            <IconButton
              onClick={() => {
                setOpenProgressDialog(false);
                setViewingProgressMD("");
              }}
            >
              <CloseIcon />
            </IconButton>
          </MDBox>
        </DialogTitle>
        <DialogContent>
          <MDBox pt={2} pb={3} px={3}>
            {viewingProgressMD ? (
              <MDBox
                sx={{
                  "& h2": {
                    fontSize: "1.5rem",
                    fontWeight: "bold",
                    marginTop: "1rem",
                    marginBottom: "0.5rem",
                  },
                  "& h3": {
                    fontSize: "1.25rem",
                    fontWeight: "bold",
                    marginTop: "1rem",
                    marginBottom: "0.5rem",
                  },
                  "& p": {
                    marginBottom: "0.5rem",
                  },
                  "& ul": {
                    marginLeft: "1.5rem",
                    marginBottom: "0.5rem",
                  },
                  "& li": {
                    marginBottom: "0.25rem",
                  },
                  "& strong": {
                    fontWeight: "bold",
                  },
                }}
              >
                <MDEditor
                  value={viewingProgressMD}
                  readOnly
                  preview="preview"
                />
              </MDBox>
            ) : (
              <MDTypography variant="body2" color="text.secondary">
                Tidak ada data progress
              </MDTypography>
            )}
          </MDBox>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenProgressDialog(false);
              setViewingProgressMD("");
            }}
            variant="contained"
            color="primary"
          >
            Tutup
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Daftar Peserta */}
      <Dialog
        open={openPesertaDialog}
        onClose={handleClosePesertaDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <MDBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <MDTypography variant="h6">Daftar Peserta</MDTypography>
            <IconButton onClick={handleClosePesertaDialog}>
              <CloseIcon />
            </IconButton>
          </MDBox>
        </DialogTitle>
        <DialogContent>
          <MDBox pt={2} pb={3} px={3}>
            {loadingPeserta ? (
              <MDBox
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight="200px"
              >
                <CircularProgress />
              </MDBox>
            ) : daftarPeserta.length === 0 ? (
              <MDBox
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight="200px"
              >
                <MDTypography variant="body2" color="text.secondary">
                  Tidak ada peserta
                </MDTypography>
              </MDBox>
            ) : (
              <MDBox>
                <DataTable
                  table={{
                    columns: [
                      { Header: "No", accessor: "no", width: "5%" },
                      {
                        Header: "Nama",
                        accessor: "nama",
                        width: "25%",
                        Cell: ({ row }) => (
                          <MDBox display="flex" alignItems="center" gap={1}>
                            {row.original.uid_RSS ? (
                              <img
                                src={`https://smartdh.rotendaokab.go.id/uploads/${row.original.uid_RSS}.jpg`}
                                alt={
                                  row.original.nama_user || row.original.nama
                                }
                                style={{
                                  width: 32,
                                  height: 32,
                                  borderRadius: "50%",
                                  objectFit: "cover",
                                }}
                                onError={(e) => {
                                  e.target.style.display = "none";
                                }}
                              />
                            ) : (
                              <MDBox
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                sx={{
                                  width: 32,
                                  height: 32,
                                  borderRadius: "50%",
                                  backgroundColor: "grey.200",
                                  color: "grey.500",
                                }}
                              >
                                <PersonIcon sx={{ fontSize: 20 }} />
                              </MDBox>
                            )}
                            <MDTypography variant="caption" color="text">
                              {row.original.nama_user ||
                                row.original.nama ||
                                "-"}
                            </MDTypography>
                          </MDBox>
                        ),
                      },
                      {
                        Header: "Instansi",
                        accessor: "instansi",
                        width: "200px",
                        Cell: ({ value }) => (
                          <MDBox
                            sx={{
                              whiteSpace: "normal",
                              wordWrap: "break-word",
                              width: "200px",
                              py: 1,
                            }}
                          >
                            <MDTypography variant="caption" color="text">
                              {value || "-"}
                            </MDTypography>
                          </MDBox>
                        ),
                      },
                      {
                        Header: "Waktu",
                        accessor: "waktu",
                        width: "20%",
                        Cell: ({ value }) => (
                          <MDTypography variant="caption" color="text">
                            {value ? formatDateTime(value) : "-"}
                          </MDTypography>
                        ),
                      },
                    ],
                    rows: daftarPeserta.map((peserta, index) => ({
                      no: index + 1,
                      nama: peserta.nama_user || peserta.nama,
                      instansi: peserta.instansi,
                      waktu: peserta.waktu,
                      uid_RSS: peserta.uid_RSS,
                      nama_user: peserta.nama_user,
                    })),
                  }}
                  isSorted={false}
                  entriesPerPage={false}
                  showTotalEntries={false}
                  noEndBorder
                />
              </MDBox>
            )}
          </MDBox>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleClosePesertaDialog}
            variant="contained"
            color="primary"
          >
            Tutup
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Edit Detail Event */}
      <Dialog
        open={openEditDetailDialog}
        onClose={handleCloseEditDetailDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <MDBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <MDTypography variant="h6">Edit Detail Event</MDTypography>
            <IconButton onClick={handleCloseEditDetailDialog}>
              <CloseIcon />
            </IconButton>
          </MDBox>
        </DialogTitle>
        <DialogContent>
          <MDBox pt={2} pb={3} px={3}>
            <MDBox mb={3}>
              <MDBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                <MDTypography
                  component="label"
                  variant="button"
                  fontWeight="regular"
                  color="text"
                >
                  Detail Kegiatan
                </MDTypography>
              </MDBox>
              <MDInput
                type="text"
                fullWidth
                value={detailFormData.detail_kegiatan}
                onChange={(e) =>
                  handleDetailFormChange("detail_kegiatan", e.target.value)
                }
                placeholder="Masukkan detail kegiatan"
              />
            </MDBox>

            <MDBox mb={3}>
              <MDBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                <MDTypography
                  component="label"
                  variant="button"
                  fontWeight="regular"
                  color="text"
                >
                  Waktu Mulai <span style={{ color: "red" }}>*</span>
                </MDTypography>
              </MDBox>
              <MDInput
                type="datetime-local"
                fullWidth
                value={detailFormData.waktu_mulai}
                onChange={(e) =>
                  handleDetailFormChange("waktu_mulai", e.target.value)
                }
                required
              />
            </MDBox>

            <MDBox mb={3}>
              <MDBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                <MDTypography
                  component="label"
                  variant="button"
                  fontWeight="regular"
                  color="text"
                >
                  Waktu Selesai
                </MDTypography>
              </MDBox>
              <MDInput
                type="datetime-local"
                fullWidth
                value={detailFormData.waktu_finish}
                onChange={(e) =>
                  handleDetailFormChange("waktu_finish", e.target.value)
                }
              />
            </MDBox>

            {/* Pilih Dinas Peserta - hanya untuk evaluasi/asistensi */}
            {editingDetailEvent &&
              (editingDetailEvent.status_event === "evaluasi" ||
                editingDetailEvent.status_event === "asistensi") && (
                <MDBox mb={3}>
                  <MDBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                    <MDTypography
                      component="label"
                      variant="button"
                      fontWeight="regular"
                      color="text"
                    >
                      Dinas Peserta <span style={{ color: "red" }}>*</span>
                    </MDTypography>
                  </MDBox>
                  <Autocomplete
                    options={targetDinasForEdit.filter(
                      (d) => d.dinas_id && d.nama_dinas
                    )}
                    value={
                      targetDinasForEdit.find(
                        (d) => d.dinas_id === detailFormData.dinas_id
                      ) || null
                    }
                    onChange={(event, newValue) =>
                      handleDetailFormChange(
                        "dinas_id",
                        newValue ? newValue.dinas_id : null
                      )
                    }
                    getOptionLabel={(option) => option.nama_dinas || ""}
                    isOptionEqualToValue={(option, value) =>
                      option.dinas_id === value?.dinas_id
                    }
                    renderInput={(params) => (
                      <MDInput
                        {...params}
                        placeholder="Pilih dinas peserta"
                        fullWidth
                        required
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props} key={option.dinas_id}>
                        {truncateDinasName(option.nama_dinas)}
                      </li>
                    )}
                    disabled={targetDinasForEdit.length === 0}
                    noOptionsText="Tidak ada dinas target untuk event ini"
                  />
                </MDBox>
              )}
            <MDBox mb={3}>
              <MDBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                <MDTypography
                  component="label"
                  variant="button"
                  fontWeight="regular"
                  color="text"
                >
                  Tempat/Lokasi
                </MDTypography>
              </MDBox>
              <Autocomplete
                freeSolo
                options={dinasList}
                value={detailFormData.tempat}
                onChange={(event, newValue) => {
                  handleDetailFormChange(
                    "tempat",
                    newValue
                      ? typeof newValue === "string"
                        ? newValue
                        : newValue.label || newValue
                      : ""
                  );
                }}
                onInputChange={(event, newInputValue) => {
                  handleDetailFormChange("tempat", newInputValue);
                }}
                renderInput={(params) => (
                  <MDInput
                    {...params}
                    placeholder="Masukkan atau pilih tempat"
                    fullWidth
                  />
                )}
              />
            </MDBox>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseEditDetailDialog}
            disabled={submittingDetail}
          >
            Batal
          </Button>
          <Button
            onClick={handleSubmitEditDetail}
            variant="contained"
            color="info"
            disabled={submittingDetail}
            startIcon={
              submittingDetail ? (
                <CircularProgress size={16} color="inherit" />
              ) : null
            }
          >
            {submittingDetail ? "Menyimpan..." : "Simpan"}
          </Button>
        </DialogActions>
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
}

export default EventSaya;
